import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription, TimeoutError } from 'rxjs';
import { finalize, timeout } from 'rxjs/operators';
import { MessageHandlerService } from 'src/app/services/message-handler.service';
import { ProgressBarService } from 'src/app/services/progress-bar.service';
import { LogshipStatusService } from './logship-status.service';
import {
  LOGSHIP_STAGE_LABELS,
  LOGSHIP_STAGE_ORDER,
  LogshipCompanyRow,
  LogshipFileStatus,
  LogshipHealth,
  LogshipStageCell,
  LogshipStageKey,
  LogshipTypeRow,
  fileTime,
  formatAge,
  isInformationalStage,
  mapLogshipStatusResponse,
} from './logship-status.model';

const FILE_STATUS_LABELS: Record<LogshipFileStatus, string> = {
  loaded: 'Loaded',
  skipped: 'Skipped',
  failed: 'Failed',
  partial: 'Partly loaded',
  normalized: 'Normalized',
  merged: 'Merged',
  scored: 'Scored',
};

/** A backend that never answers ends the load here, instead of leaving the spinner up. */
export const LOGSHIP_LOAD_TIMEOUT_MS = 30_000;

const LOAD_FAILED = 'Failed to load Logship status';

/** 0 is no answer at all (down, or a gateway error without CORS headers); the rest are gateway errors. */
const SERVICE_DOWN_STATUSES = [0, 502, 503, 504];

const PROCESSED_HEADERS: Record<LogshipStageKey, string> = {
  etl: 'Picked up',
  normalized: 'Normalized',
  merged: 'Merged',
  ioiq: 'Processed',
};

@Component({
  selector: 'app-logship-status',
  templateUrl: './logship-status.component.html',
  styleUrls: ['./logship-status.component.scss'],
})
export class LogshipStatusComponent implements OnInit, OnDestroy {
  companies: LogshipCompanyRow[] = [];
  isLoading = false;
  errorMessage = '';
  lastCheckedAt: Date | null = null;

  readonly stageLabels = LOGSHIP_STAGE_ORDER.map((key) => LOGSHIP_STAGE_LABELS[key]);

  /** The cell whose More is open, with its company. */
  moreOpen = false;
  moreCompany: LogshipCompanyRow | null = null;
  moreCell: LogshipStageCell | null = null;

  /**
   * Ages are computed against the timestamp of the last load rather than a live clock, so the
   * table does not drift while it sits open. The view reloads whenever the Logship tab is
   * activated (and on demand via Refresh), which is what keeps it current.
   */
  private evaluatedAt: Date = new Date();

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly logshipStatusService: LogshipStatusService,
    private readonly messageHandlerService: MessageHandlerService,
    private readonly progressBarService: ProgressBarService,
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  /** Called on init, whenever the Logship tab is (re)activated, and by the Refresh button. */
  reload(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.errorMessage = '';
    this.progressBarService.setProgressBarVisibility(true);

    // `finalize` clears the spinner and the progress bar however the call ends: an answer, an error, the timeout, or a
    // completion with no answer. Resetting isLoading only inside next and error once left it true
    // forever, which also disabled Refresh and made `if (this.isLoading) return` above swallow every
    // later reload. ErrorInterceptor now passes this endpoint's errors through (with their status),
    // but the no-answer path below stays as a guard.
    let answered = false;

    this.subscriptions.push(
      this.logshipStatusService
        .getLogshipStatus()
        .pipe(
          timeout(LOGSHIP_LOAD_TIMEOUT_MS),
          finalize(() => {
            this.isLoading = false;
            this.progressBarService.setProgressBarVisibility(false);
          }),
        )
        .subscribe({
          next: (response) => {
            answered = true;
            // A 200 carrying success: false (the backend's try/catch branch) lands here,
            // not in the error callback, and must not be rendered as an empty state.
            if (!response?.success) {
              this.showError(response?.message || LOAD_FAILED);
              return;
            }
            this.evaluatedAt = new Date();
            this.lastCheckedAt = this.evaluatedAt;
            this.companies = mapLogshipStatusResponse(response?.data, this.evaluatedAt);
          },
          error: (error) => {
            answered = true;
            this.showError(this.describeError(error));
          },
          complete: () => {
            // Completed with no answer. Without this the panel falls through to the empty
            // state, which reads as "nothing is being reported" — the opposite of what happened.
            if (!answered) {
              this.errorMessage = LOAD_FAILED;
              this.companies = [];
            }
          },
        }),
    );
  }

  /** In the panel, and as a toast: a Refresh can fail while the panel is scrolled out of view. */
  private showError(message: string): void {
    this.errorMessage = message;
    this.companies = [];
    this.messageHandlerService.generateToastMessage('error', '', message);
  }

  private describeError(error: any): string {
    if (error instanceof TimeoutError) {
      return 'Logship status took too long to load. Please try again.';
    }
    if (SERVICE_DOWN_STATUSES.includes(error?.status)) {
      const status = error.status ? ` (${error.status})` : '';
      return `Logship status is unavailable right now${status}. Please try again in a few minutes.`;
    }
    const serverMessage = error?.error?.message || error?.error?.errorMessage;
    if (typeof serverMessage === 'string' && serverMessage) return serverMessage;
    if (typeof error === 'string' && error) return error;
    // Not HttpErrorResponse.message: that is "Http failure response for <url>: ...".
    return error?.status ? `${LOAD_FAILED} (${error.status})` : LOAD_FAILED;
  }

  openMore(company: LogshipCompanyRow, cell: LogshipStageCell): void {
    this.moreCompany = company;
    this.moreCell = cell;
    this.moreOpen = true;
  }

  closeMore(): void {
    this.moreOpen = false;
    this.moreCompany = null;
    this.moreCell = null;
  }

  get moreHeader(): string {
    return this.moreCompany && this.moreCell ? `${this.moreCompany.company} · ${this.moreCell.label}` : '';
  }

  get processedHeader(): string {
    return this.moreCell ? PROCESSED_HEADERS[this.moreCell.key] : 'Processed';
  }

  age(timestamp: Date | null): string {
    return formatAge(timestamp, this.evaluatedAt);
  }

  cellTime(cell: LogshipStageCell): Date | null {
    return fileTime(cell.latest);
  }

  rowTime(row: LogshipTypeRow): Date | null {
    return fileTime(row.file);
  }

  healthLabel(health: LogshipHealth): string {
    switch (health) {
      case 'healthy':
        return 'Fresh';
      case 'warning':
        return 'Delayed';
      case 'stale':
        return 'Stale';
      default:
        return 'No data';
    }
  }

  statusLabel(status: LogshipFileStatus | null | undefined): string {
    return status ? FILE_STATUS_LABELS[status] : '';
  }

  rowStatus(row: LogshipTypeRow): string {
    if (row.expectation === 'retired') return 'Retired - no longer delivered';
    if (row.expectation === 'not_expected') return 'Not expected for this company';
    if (row.note) return row.note;
    if (!row.file) return 'No file in the last 24h';
    return this.statusLabel(row.file.status);
  }

  /** Text alternative, so a cell's state is never conveyed by colour alone. */
  cellTooltip(cell: LogshipStageCell): string {
    // An informational stage has no health to report, so say what it does hold instead.
    const parts = [`${cell.label}: ${isInformationalStage(cell.key) ? 'last run' : this.healthLabel(cell.health)}`];
    const latest = cell.latest;
    if (!latest) {
      parts.push('nothing recorded yet');
    } else {
      if (latest.fileName) parts.push(latest.fileName);
      // Processed first: it is what the colour goes by. The file-name time follows, when there is one.
      if (latest.processedAt) parts.push(`${this.statusLabel(latest.status).toLowerCase()} ${this.age(latest.processedAt)}`);
      if (latest.arrivedAt) parts.push(`arrived ${this.age(latest.arrivedAt)}`);
    }
    if (cell.failingSince) parts.push(`failing since ${this.age(cell.failingSince)}`);
    // Informational stages have no More dialog, so their note belongs on the hover.
    if (isInformationalStage(cell.key) && cell.note) parts.push(cell.note);
    return parts.join(' · ');
  }

  /** ioIQ shows the last engine run: no colour of its own, and no bearing on the company status. */
  isInformational(cell: LogshipStageCell): boolean {
    return isInformationalStage(cell.key);
  }

  trackByCompany(_index: number, company: LogshipCompanyRow): string {
    return company.companyId;
  }

  trackByType(_index: number, row: LogshipTypeRow): string {
    return row.fileType;
  }
}
